<script setup lang="ts">
import type { ChannelWithReleases, Product } from '@/types'
import { Link, router, useForm } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import ReleaseFormFields from '@/components/releases/ReleaseFormFields.vue'
import ReleaseStateBadge from '@/components/releases/ReleaseStateBadge.vue'
import { firstFormError } from '@/lib/forms'
import {
  adminChannelPath,
  adminChannelReleasesPath,
  adminProductChannelsPath,
  adminReleasePath,
} from '@/routes'

const props = defineProps<{
  product: Pick<Product, 'id' | 'slug' | 'name'>
  channels: ChannelWithReleases[]
}>()

interface ChannelFormData {
  name: string
  minimum_supported_version: string
  require_signed_releases: boolean
  release_source: ChannelWithReleases['release_source']
  github_repository: string
  github_repository_id: string
  github_repository_owner_id: string
  github_workflow: string
  github_branch: string
  github_environment: string
}

const emptyChannelForm: ChannelFormData = {
  name: '',
  minimum_supported_version: '',
  require_signed_releases: false,
  release_source: 'manual',
  github_repository: '',
  github_repository_id: '',
  github_repository_owner_id: '',
  github_workflow: '',
  github_branch: '',
  github_environment: '',
}

const channelOpen = ref(false)
const editingChannel = ref<ChannelWithReleases | null>(null)
const channelForm = useForm({ ...emptyChannelForm })

const ghCliHint = computed(() => {
  const repo = channelForm.github_repository.trim()
    .replace(/^https?:\/\/(www\.)?github\.com\//i, '')
    .toLowerCase()
  return repo || 'owner/name'
})

function openNewChannel() {
  editingChannel.value = null
  channelForm.defaults({ ...emptyChannelForm })
  channelForm.reset()
  channelForm.clearErrors()
  channelOpen.value = true
}

function openEditChannel(channel: ChannelWithReleases) {
  editingChannel.value = channel
  channelForm.defaults({
    name: channel.name,
    minimum_supported_version: channel.minimum_supported_version ?? '',
    require_signed_releases: channel.require_signed_releases,
    release_source: channel.release_source,
    github_repository: channel.github_repository ?? '',
    github_repository_id: channel.github_repository_id ?? '',
    github_repository_owner_id: channel.github_repository_owner_id ?? '',
    github_workflow: channel.github_workflow ?? '',
    github_branch: channel.github_branch ?? '',
    github_environment: channel.github_environment ?? '',
  })
  channelForm.reset()
  channelForm.clearErrors()
  channelOpen.value = true
}

function submitChannel() {
  const onSuccess = () => (channelOpen.value = false)

  if (editingChannel.value) {
    channelForm.patch(adminChannelPath(editingChannel.value.id), {
      preserveScroll: true,
      onSuccess,
    })
  }
  else {
    channelForm.post(adminProductChannelsPath(props.product.slug), {
      preserveScroll: true,
      onSuccess,
    })
  }
}

function canCreateManualRelease(channel: ChannelWithReleases) {
  return channel.release_source === 'manual'
}

const deleteChannelTarget = ref<ChannelWithReleases | null>(null)
const deletingChannel = ref(false)

function updateDeleteChannelOpen(value: boolean) {
  if (!value)
    deleteChannelTarget.value = null
}

function destroyChannel() {
  if (!deleteChannelTarget.value)
    return

  router.delete(adminChannelPath(deleteChannelTarget.value.id), {
    preserveScroll: true,
    onStart: () => (deletingChannel.value = true),
    onFinish: () => {
      deletingChannel.value = false
      deleteChannelTarget.value = null
    },
  })
}

const releaseOpen = ref(false)
const releaseChannel = ref<ChannelWithReleases | null>(null)
const releaseForm = useForm({ version: '', changelog: '' })

function openNewRelease(channel: ChannelWithReleases) {
  releaseChannel.value = channel
  releaseForm.reset()
  releaseForm.clearErrors()
  releaseOpen.value = true
}

function submitRelease() {
  if (!releaseChannel.value)
    return

  releaseForm.post(adminChannelReleasesPath(releaseChannel.value.id), {
    preserveScroll: true,
    onSuccess: () => (releaseOpen.value = false),
  })
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-end justify-between gap-4">
      <div>
        <h2 class="text-highlighted font-semibold">
          Channels &amp; releases
        </h2>
        <p class="text-muted text-sm">
          Distribution channels and the versions published to them.
        </p>
      </div>
      <UButton label="New channel" icon="i-lucide-plus" size="sm" @click="openNewChannel" />
    </div>

    <div
      v-if="channels.length === 0"
      class="ring-default text-muted rounded-lg p-10 text-center text-sm ring"
    >
      No channels yet. Create one (e.g. <span class="font-mono">stable</span>) to start publishing releases.
    </div>

    <div
      v-for="channel in channels"
      :key="channel.id"
      class="ring-default flex flex-col gap-4 rounded-lg p-5 ring"
    >
      <div class="flex items-start justify-between gap-4">
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-git-branch" class="text-muted size-4 shrink-0" />
            <span class="text-highlighted font-mono font-medium">{{ channel.name }}</span>
            <span v-if="channel.minimum_supported_version" class="text-muted font-mono text-xs">
              min.&nbsp;{{ channel.minimum_supported_version }}
            </span>
            <UBadge
              v-if="channel.require_signed_releases"
              color="success"
              variant="subtle"
              size="xs"
              label="signed required"
            />
          </div>

          <div v-if="channel.release_source === 'github_actions'" class="text-muted flex items-center gap-1.5 text-xs">
            <UIcon name="i-lucide-github" class="size-3.5 shrink-0" />
            <span class="font-mono">{{ channel.github_repository }}</span>
            <template v-if="channel.github_workflow && channel.github_branch">
              <span class="select-none">·</span>
              <span class="font-mono">{{ channel.github_workflow }}@{{ channel.github_branch }}</span>
            </template>
            <template v-if="channel.github_environment">
              <span class="select-none">·</span>
              <UBadge color="neutral" variant="subtle" size="xs" :label="channel.github_environment" />
            </template>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <UButton
            label="New release"
            icon="i-lucide-plus"
            size="sm"
            variant="subtle"
            color="neutral"
            :disabled="!canCreateManualRelease(channel)"
            :title="canCreateManualRelease(channel) ? 'Create a manual release' : 'GitHub Actions channels publish through trusted OIDC workflows'"
            @click="openNewRelease(channel)"
          />
          <UButton
            icon="i-lucide-pencil"
            size="sm"
            color="neutral"
            variant="ghost"
            square
            aria-label="Edit channel"
            @click="openEditChannel(channel)"
          />
          <UButton
            icon="i-lucide-trash-2"
            size="sm"
            color="neutral"
            variant="ghost"
            square
            aria-label="Delete channel"
            @click="deleteChannelTarget = channel"
          />
        </div>
      </div>

      <div
        v-if="channel.releases.length === 0"
        class="text-muted text-sm"
      >
        No releases in this channel yet.
      </div>

      <ul v-else class="divide-default divide-y">
        <li
          v-for="release in channel.releases"
          :key="release.id"
          class="flex items-center justify-between gap-4 py-2"
        >
          <Link
            :href="adminReleasePath(release.id)"
            class="text-highlighted font-mono font-medium hover:underline"
          >
            {{ release.version }}
          </Link>
          <div class="flex items-center gap-3">
            <span class="text-muted text-xs">
              {{ release.artifacts_count }} {{ release.artifacts_count === 1 ? 'artifact' : 'artifacts' }}
            </span>
            <UBadge
              v-if="release.supported === false"
              color="error"
              variant="subtle"
              label="EOL"
            />
            <ReleaseStateBadge :state="release.state" />
          </div>
        </li>
      </ul>
    </div>
  </div>

  <!-- Channel create / edit modal -->
  <UModal
    v-model:open="channelOpen"
    :title="editingChannel ? 'Edit channel' : 'New channel'"
    size="xl"
  >
    <template #body>
      <div class="flex flex-col gap-5">
        <div class="grid gap-4 sm:grid-cols-2">
          <UFormField
            label="Name"
            name="name"
            :error="firstFormError(channelForm.errors.name)"
            required
          >
            <UInput
              v-model="channelForm.name"
              placeholder="stable"
              icon="i-lucide-git-branch"
              class="w-full font-mono"
            />
          </UFormField>

          <UFormField
            label="Minimum supported version"
            name="minimum_supported_version"
            :error="firstFormError(channelForm.errors.minimum_supported_version)"
          >
            <UInput
              v-model="channelForm.minimum_supported_version"
              placeholder="2.0.0 (optional)"
              icon="i-lucide-shield-check"
              class="w-full font-mono"
            />
          </UFormField>
        </div>

        <UFormField
          label="Require signed releases"
          name="require_signed_releases"
          :error="firstFormError(channelForm.errors.require_signed_releases)"
          description="Publishing is blocked unless the release has a valid descriptor signature."
        >
          <USwitch v-model="channelForm.require_signed_releases" />
        </UFormField>

        <UFormField
          label="Release source"
          name="release_source"
          :error="firstFormError(channelForm.errors.release_source)"
          required
        >
          <USelect
            v-model="channelForm.release_source"
            :items="[
              { label: 'Manual publishing', value: 'manual' },
              { label: 'GitHub Actions trusted publishing', value: 'github_actions' },
            ]"
            class="w-full"
          />
        </UFormField>

        <div v-if="channelForm.release_source === 'github_actions'" class="border-default flex flex-col gap-4 rounded-xl border p-4">
          <UFormField
            label="Repository"
            name="github_repository"
            :error="firstFormError(channelForm.errors.github_repository)"
            required
          >
            <UInput
              v-model="channelForm.github_repository"
              placeholder="zerya/motoq"
              icon="i-lucide-github"
              class="w-full font-mono"
            />
          </UFormField>

          <div class="bg-muted/30 flex items-start gap-2 rounded-lg p-3">
            <UIcon name="i-lucide-terminal" class="text-muted mt-px size-3.5 shrink-0" />
            <div class="min-w-0 font-mono text-xs">
              <span class="text-muted select-none">$ </span>
              <span class="text-highlighted break-all">gh api repos/{{ ghCliHint }} --jq '[.id, .owner.id]'</span>
            </div>
          </div>

          <div class="grid gap-4 sm:grid-cols-2">
            <UFormField
              label="Repository ID"
              name="github_repository_id"
              :error="firstFormError(channelForm.errors.github_repository_id)"
              required
            >
              <UInput
                v-model="channelForm.github_repository_id"
                placeholder="123456789"
                class="w-full font-mono"
              />
            </UFormField>

            <UFormField
              label="Owner ID"
              name="github_repository_owner_id"
              :error="firstFormError(channelForm.errors.github_repository_owner_id)"
              required
            >
              <UInput
                v-model="channelForm.github_repository_owner_id"
                placeholder="987654321"
                class="w-full font-mono"
              />
            </UFormField>
          </div>

          <div class="grid gap-4 sm:grid-cols-2">
            <UFormField
              label="Workflow file"
              name="github_workflow"
              :error="firstFormError(channelForm.errors.github_workflow)"
              required
            >
              <UInput
                v-model="channelForm.github_workflow"
                placeholder="release.yml"
                class="w-full font-mono"
              />
            </UFormField>

            <UFormField
              label="Branch"
              name="github_branch"
              :error="firstFormError(channelForm.errors.github_branch)"
              required
            >
              <UInput
                v-model="channelForm.github_branch"
                placeholder="main"
                class="w-full font-mono"
              />
            </UFormField>
          </div>

          <UFormField
            label="Environment"
            name="github_environment"
            :error="firstFormError(channelForm.errors.github_environment)"
          >
            <UInput
              v-model="channelForm.github_environment"
              placeholder="production (optional)"
              class="w-full font-mono"
            />
          </UFormField>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton label="Cancel" color="neutral" variant="ghost" @click="channelOpen = false" />
        <UButton
          :label="editingChannel ? 'Save changes' : 'Create channel'"
          icon="i-lucide-save"
          :loading="channelForm.processing"
          :disabled="!channelForm.name"
          @click="submitChannel"
        />
      </div>
    </template>
  </UModal>

  <!-- Release create modal -->
  <UModal
    v-model:open="releaseOpen"
    title="New release"
    :description="releaseChannel ? `Add a version to the ${releaseChannel.name} channel.` : ''"
  >
    <template #body>
      <ReleaseFormFields
        v-model:version="releaseForm.version"
        v-model:changelog="releaseForm.changelog"
        :errors="releaseForm.errors"
      />
    </template>

    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton label="Cancel" color="neutral" variant="ghost" @click="releaseOpen = false" />
        <UButton
          label="Create release"
          icon="i-lucide-plus"
          :loading="releaseForm.processing"
          :disabled="!releaseForm.version"
          @click="submitRelease"
        />
      </div>
    </template>
  </UModal>

  <!-- Delete channel confirm -->
  <UModal
    :open="deleteChannelTarget !== null"
    title="Delete channel"
    :description="deleteChannelTarget ? `Delete the &quot;${deleteChannelTarget.name}&quot; channel? Channels with releases cannot be deleted.` : ''"
    @update:open="updateDeleteChannelOpen"
  >
    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton label="Cancel" color="neutral" variant="ghost" @click="deleteChannelTarget = null" />
        <UButton
          label="Delete"
          color="error"
          icon="i-lucide-trash-2"
          :loading="deletingChannel"
          @click="destroyChannel"
        />
      </div>
    </template>
  </UModal>
</template>
